import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { CrmDataProvider } from "../providers/types";
import type { Sale } from "../types";

/**
 * Almikia Epic EW3 (TASK-095): the `can_classify_whatsapp_contacts` switch of the team edit area.
 * Rendered ONLY for direccion and never on her own row (the RPC refuses both anyway). It saves at
 * once through `setCanClassifyWhatsAppContacts`, not through the user form.
 */
export const ClassifyPermissionSwitch = ({ sale }: { sale: Sale }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const [checked, setChecked] = useState(
    sale.can_classify_whatsapp_contacts === true,
  );
  useEffect(() => {
    setChecked(sale.can_classify_whatsapp_contacts === true);
  }, [sale.can_classify_whatsapp_contacts]);

  const { mutate, isPending } = useMutation({
    mutationFn: (value: boolean) =>
      dataProvider.setCanClassifyWhatsAppContacts({
        salesId: sale.id,
        value,
      }),
    onSuccess: (value) => {
      setChecked(value);
      notify(
        value
          ? "crm.whatsapp.classify_permission.on"
          : "crm.whatsapp.classify_permission.off",
      );
    },
    onError: (error) => {
      notify(
        error instanceof Error && error.message
          ? error.message
          : "crm.whatsapp.classify_permission.error",
        { type: "error" },
      );
    },
  });

  if (identity?.role !== "direccion" || identity.id === sale.id) return null;

  return (
    <Card data-testid="classify-permission-card">
      <CardHeader>
        <CardTitle className="text-base">
          {translate("crm.whatsapp.classify_permission.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex min-h-11 items-center gap-3">
          <Switch
            id="can-classify-whatsapp-contacts"
            checked={checked}
            disabled={isPending}
            onCheckedChange={(value) => mutate(value)}
          />
          <Label htmlFor="can-classify-whatsapp-contacts">
            {translate("crm.whatsapp.classify_permission.label")}
          </Label>
        </div>
        <p className="text-sm text-muted-foreground">
          {translate("crm.whatsapp.classify_permission.help")}
        </p>
      </CardContent>
    </Card>
  );
};
